import React, { useState, useEffect, useCallback } from 'react';
import './TimeTracking.css';
import logo from '../assets/Zenio+Logo+header.png';
import { api, useLocalStorageFallback } from '../services/api';

function TimeTracking({ user, onBack }) {
  console.log('TimeTracking mounted with user:', user);
  console.log('user.userId:', user.userId);
  console.log('user.username:', user.username);
  
  const [timeRecords, setTimeRecords] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('clocked-out'); // 'clocked-in', 'on-break', 'clocked-out'
  const [todayRecord, setTodayRecord] = useState(null);
  const [selectedUser, setSelectedUser] = useState(user.userId || user.username);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const useFallback = useLocalStorageFallback();
  
  console.log('Using fallback (localStorage):', useFallback);
  console.log('Selected user on mount:', user.userId || user.username);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const loadUsers = useCallback(async () => {
    if (!user.isAdmin) return;
    
    if (useFallback) {
      const savedUsers = localStorage.getItem('zenio_users');
      if (savedUsers) {
        setUsers(JSON.parse(savedUsers));
      }
    } else {
      try {
        const result = await api.getUsers();
        if (result.success) {
          setUsers(result.users || []);
        }
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    }
  }, [user.isAdmin, useFallback]);

  const loadTimeRecords = useCallback(async () => {
    setIsLoading(true);
    
    if (useFallback) {
      const saved = localStorage.getItem('zenio_timetracking');
      const allRecords = saved ? JSON.parse(saved) : [];
      
      console.log('All records:', allRecords);
      console.log('Selected user:', selectedUser);
      console.log('Current user:', user.userId || user.username);
      
      // Filter by selected user
      const userRecords = allRecords.filter(r => r.userId === selectedUser);
      console.log('User records:', userRecords);
      setTimeRecords(userRecords);
      
      // Find today's record - use createdAt or clockIn as fallback
      const today = new Date().toDateString();
      const todaysRec = userRecords.find(r => {
        const recordDate = r.date || r.createdAt || r.clockIn;
        return recordDate && new Date(recordDate).toDateString() === today;
      });
      console.log('Today\'s record:', todaysRec);
      setTodayRecord(todaysRec || null);
      
      if (todaysRec) {
        // Handle both localStorage format (clockOut, onBreak) and API format (status)
        if (todaysRec.status) {
          setCurrentStatus(todaysRec.status);
        } else if (todaysRec.clockOut) {
          setCurrentStatus('clocked-out');
        } else if (todaysRec.onBreak) {
          setCurrentStatus('on-break');
        } else {
          setCurrentStatus('clocked-in');
        }
      } else {
        setCurrentStatus('clocked-out');
      }
      
      calculateStats(userRecords);
      setIsLoading(false);
    } else {
      try {
        const result = await api.getTimeRecords(selectedUser);
        if (result.success) {
          setTimeRecords(result.records || []);
          const today = new Date().toDateString();
          const todaysRec = (result.records || []).find(r => {
            const recordDate = r.date || r.createdAt || r.clockIn;
            return recordDate && new Date(recordDate).toDateString() === today;
          });
          setTodayRecord(todaysRec || null);
          
          if (todaysRec) {
            // Handle both localStorage format (clockOut, onBreak) and API format (status)
            if (todaysRec.status) {
              setCurrentStatus(todaysRec.status);
            } else if (todaysRec.clockOut) {
              setCurrentStatus('clocked-out');
            } else if (todaysRec.onBreak) {
              setCurrentStatus('on-break');
            } else {
              setCurrentStatus('clocked-in');
            }
          } else {
            setCurrentStatus('clocked-out');
          }
          
          calculateStats(result.records || []);
        }
      } catch (error) {
        console.error('Failed to load records:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [selectedUser, useFallback]);

  useEffect(() => {
    loadUsers();
    loadTimeRecords();
  }, [loadUsers, loadTimeRecords]);

  const calculateStats = (records) => {
    if (records.length === 0) {
      setStats({
        avgClockIn: 'N/A',
        avgClockOut: 'N/A',
        avgBreakTime: 'N/A',
        totalHours: 0
      });
      return;
    }

    const completedDays = records.filter(r => r.clockIn && r.clockOut);
    
    if (completedDays.length === 0) {
      setStats({
        avgClockIn: 'N/A',
        avgClockOut: 'N/A',
        avgBreakTime: 'N/A',
        totalHours: 0
      });
      return;
    }

    // Calculate averages
    const avgClockInMs = completedDays.reduce((sum, r) => sum + new Date(r.clockIn).getTime(), 0) / completedDays.length;
    const avgClockOutMs = completedDays.reduce((sum, r) => sum + new Date(r.clockOut).getTime(), 0) / completedDays.length;
    
    const avgClockInTime = new Date(avgClockInMs);
    const avgClockOutTime = new Date(avgClockOutMs);
    
    const totalBreakMs = records.reduce((sum, r) => sum + (r.totalBreakTime || 0), 0);
    const avgBreakMs = totalBreakMs / records.length;
    
    const totalWorkMs = completedDays.reduce((sum, r) => {
      const workMs = new Date(r.clockOut).getTime() - new Date(r.clockIn).getTime() - (r.totalBreakTime || 0);
      return sum + workMs;
    }, 0);

    setStats({
      avgClockIn: avgClockInTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      avgClockOut: avgClockOutTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      avgBreakTime: formatDuration(avgBreakMs),
      totalHours: (totalWorkMs / (1000 * 60 * 60)).toFixed(1)
    });
  };

  const formatDuration = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const handleClockIn = async () => {
    // Only allow clocking in for yourself
    if (selectedUser !== (user.userId || user.username)) {
      alert('You can only clock in/out for yourself.');
      return;
    }
    
    const now = new Date().toISOString();
    const record = {
      recordId: Date.now().toString(),
      userId: selectedUser,
      date: new Date().toISOString(),
      clockIn: now,
      clockOut: null,
      breaks: [],
      totalBreakTime: 0,
      onBreak: false
    };

    if (useFallback) {
      const saved = localStorage.getItem('zenio_timetracking');
      const allRecords = saved ? JSON.parse(saved) : [];
      allRecords.push(record);
      localStorage.setItem('zenio_timetracking', JSON.stringify(allRecords));
      setTodayRecord(record);
      setCurrentStatus('clocked-in');
      // Reload to update stats and history
      setTimeout(() => loadTimeRecords(), 100);
    } else {
      try {
        const result = await api.clockIn(user.userId || user.username);
        if (result.success) {
          await loadTimeRecords();
        }
      } catch (error) {
        console.error('Failed to clock in:', error);
      }
    }
  };

  const handleClockOut = async () => {
    if (!todayRecord) return;
    
    // Only allow clocking out for yourself
    if (selectedUser !== (user.userId || user.username)) {
      alert('You can only clock in/out for yourself.');
      return;
    }
    
    const now = new Date().toISOString();
    const updatedRecord = { ...todayRecord, clockOut: now };

    if (useFallback) {
      const saved = localStorage.getItem('zenio_timetracking');
      const allRecords = saved ? JSON.parse(saved) : [];
      const index = allRecords.findIndex(r => r.recordId === todayRecord.recordId);
      if (index !== -1) {
        allRecords[index] = updatedRecord;
        localStorage.setItem('zenio_timetracking', JSON.stringify(allRecords));
      }
      setTodayRecord(updatedRecord);
      setCurrentStatus('clocked-out');
      await loadTimeRecords();
    } else {
      try {
        console.log('Calling API clockOut for user:', user.userId || user.username);
        const result = await api.clockOut(user.userId || user.username);
        console.log('API clockOut result:', result);
        if (result.success) {
          await loadTimeRecords();
        } else {
          console.error('Clock out failed:', result.message);
          alert(`Failed to clock out: ${result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Failed to clock out:', error);
        alert(`Error clocking out: ${error.message}`);
      }
    }
  };

  const handleStartBreak = async () => {
    if (!todayRecord) return;
    
    // Only allow starting break for yourself
    if (selectedUser !== (user.userId || user.username)) {
      alert('You can only manage breaks for yourself.');
      return;
    }
    
    const now = new Date().toISOString();
    const updatedRecord = {
      ...todayRecord,
      breaks: [...(todayRecord.breaks || []), { start: now, end: null }],
      onBreak: true
    };

    if (useFallback) {
      const saved = localStorage.getItem('zenio_timetracking');
      const allRecords = saved ? JSON.parse(saved) : [];
      const index = allRecords.findIndex(r => r.recordId === todayRecord.recordId);
      if (index !== -1) {
        allRecords[index] = updatedRecord;
        localStorage.setItem('zenio_timetracking', JSON.stringify(allRecords));
      }
      setTodayRecord(updatedRecord);
      setCurrentStatus('on-break');
      await loadTimeRecords();
    } else {
      try {
        console.log('Calling API startBreak for user:', user.userId || user.username);
        const result = await api.startBreak(user.userId || user.username);
        console.log('API startBreak result:', result);
        if (result.success) {
          await loadTimeRecords();
        } else {
          console.error('Start break failed:', result.message);
          alert(`Failed to start break: ${result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Failed to start break:', error);
        alert(`Error starting break: ${error.message}`);
      }
    }
  };

  const handleEndBreak = async () => {
    if (!todayRecord) return;
    
    // Only allow ending break for yourself
    if (selectedUser !== (user.userId || user.username)) {
      alert('You can only manage breaks for yourself.');
      return;
    }
    
    const now = new Date().toISOString();
    const breaks = [...(todayRecord.breaks || [])];
    const lastBreak = breaks[breaks.length - 1];
    if (lastBreak) {
      lastBreak.end = now;
      const breakDuration = new Date(now).getTime() - new Date(lastBreak.start).getTime();
      const totalBreakTime = (todayRecord.totalBreakTime || 0) + breakDuration;
      
      const updatedRecord = {
        ...todayRecord,
        breaks,
        totalBreakTime,
        onBreak: false
      };

      if (useFallback) {
        const saved = localStorage.getItem('zenio_timetracking');
        const allRecords = saved ? JSON.parse(saved) : [];
        const index = allRecords.findIndex(r => r.recordId === todayRecord.recordId);
        if (index !== -1) {
          allRecords[index] = updatedRecord;
          localStorage.setItem('zenio_timetracking', JSON.stringify(allRecords));
        }
        setTodayRecord(updatedRecord);
        setCurrentStatus('clocked-in');
        await loadTimeRecords();
      } else {
        try {
          console.log('Calling API endBreak for user:', user.userId || user.username);
          const result = await api.endBreak(user.userId || user.username);
          console.log('API endBreak result:', result);
          if (result.success) {
            await loadTimeRecords();
          } else {
            console.error('End break failed:', result.message);
            alert(`Failed to end break: ${result.message || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Failed to end break:', error);
          alert(`Error ending break: ${error.message}`);
        }
      }
    }
  };

  const getTodaysHours = () => {
    if (!todayRecord || !todayRecord.clockIn) return '0h 0m';
    
    const start = new Date(todayRecord.clockIn).getTime();
    const end = todayRecord.clockOut ? new Date(todayRecord.clockOut).getTime() : currentTime.getTime();
    const breakTime = todayRecord.totalBreakTime || 0;
    
    // Add current break time if on break
    let currentBreakTime = 0;
    if (todayRecord.onBreak && todayRecord.breaks && todayRecord.breaks.length > 0) {
      const lastBreak = todayRecord.breaks[todayRecord.breaks.length - 1];
      if (lastBreak && !lastBreak.end) {
        currentBreakTime = currentTime.getTime() - new Date(lastBreak.start).getTime();
      }
    }
    
    const workMs = end - start - breakTime - currentBreakTime;
    return formatDuration(workMs);
  };

  return (
    <div className="time-tracking">
      <div className="tracking-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <div className="header-logo-section">
          <div className="header-logo-backdrop">
            <img src={logo} alt="ZenIO Logo" className="header-logo-img" />
          </div>
          <h1>Time Tracking (FOR DEMO USE ONLY)</h1>
        </div>
      </div>

      {user.isAdmin && (
        <div className="user-selector">
          <label>View records for:</label>
          <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
            <option value={user.userId || user.username}>Me ({user.name})</option>
            {users.filter(u => (u.userId || u.username) !== (user.userId || user.username)).map(u => (
              <option key={u.userId || u.username} value={u.userId || u.username}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="tracking-content">
        <div className="current-status-card">
          <h2>Current Status</h2>
          <div className="status-indicator">
            <div className={`status-dot ${currentStatus}`}></div>
            <span className="status-text">
              {currentStatus === 'clocked-in' && 'Clocked In'}
              {currentStatus === 'on-break' && 'On Break'}
              {currentStatus === 'clocked-out' && 'Clocked Out'}
            </span>
          </div>
          
          <div className="current-time">
            {/* Display current time in PST (Pacific Standard Time / America/Los_Angeles) */}
            <div className="time-display">{currentTime.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles' })}</div>
            <div className="date-display">{currentTime.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>

          {todayRecord && todayRecord.clockIn && (
            <div className="todays-hours">
              <span>Today's Hours:</span>
              <strong>{getTodaysHours()}</strong>
            </div>
          )}

          <div className="action-buttons">
            {selectedUser !== (user.userId || user.username) ? (
              <div className="viewing-other-user-notice" style={{
                padding: '10px',
                background: 'rgba(255, 193, 7, 0.2)',
                borderRadius: '8px',
                color: '#ffc107',
                textAlign: 'center',
                fontSize: '14px'
              }}>
                ℹ️ Viewing another user's records - Clock in/out buttons are disabled
              </div>
            ) : (
              <>
                {currentStatus === 'clocked-out' && (
                  <button className="clock-in-btn" onClick={handleClockIn}>
                    🕐 Clock In
                  </button>
                )}
                
                {currentStatus === 'clocked-in' && (
                  <>
                    <button className="break-btn" onClick={handleStartBreak}>
                      ☕ Start Break
                    </button>
                    <button className="clock-out-btn" onClick={handleClockOut}>
                      🕐 Clock Out
                    </button>
                  </>
                )}
                
                {currentStatus === 'on-break' && (
                  <button className="end-break-btn" onClick={handleEndBreak}>
                    ✓ End Break
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Avg Clock In</div>
              <div className="stat-value">{stats.avgClockIn}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Clock Out</div>
              <div className="stat-value">{stats.avgClockOut}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Break Time</div>
              <div className="stat-value">{stats.avgBreakTime}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Hours</div>
              <div className="stat-value">{stats.totalHours}h</div>
            </div>
          </div>
        )}

        <div className="records-section">
          <h2>Attendance History</h2>
          {isLoading ? (
            <div className="loading">Loading records...</div>
          ) : timeRecords.length === 0 ? (
            <div className="no-records">No attendance records found</div>
          ) : (
            <div className="records-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Break Time</th>
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {timeRecords.slice().reverse().map(record => {
                    const workMs = record.clockOut
                      ? new Date(record.clockOut).getTime() - new Date(record.clockIn).getTime() - (record.totalBreakTime || 0)
                      : 0;
                    
                    // Validate date - use date, createdAt, or clockIn as fallback
                    const dateValue = record.date || record.createdAt || record.clockIn;
                    const recordDate = dateValue ? new Date(dateValue) : null;
                    const isValidDate = recordDate && !isNaN(recordDate.getTime());
                    
                    return (
                      <tr key={record.recordId}>
                        <td>{isValidDate ? recordDate.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' }) : 'Invalid Date'}</td>
                        <td>{record.clockIn ? new Date(record.clockIn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }) : '-'}</td>
                        <td>{record.clockOut ? new Date(record.clockOut).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }) : 'In Progress'}</td>
                        <td>{formatDuration(record.totalBreakTime || 0)}</td>
                        <td>{record.clockOut ? formatDuration(workMs) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TimeTracking;
